import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatBlobError, formatApiErrorDetail } from "@/lib/api";
import { inr, payItem } from "@/lib/razorpay";
import { toast } from "sonner";
import Workbench from "@/pages/Workbench";
import RegistrationsPanel from "@/components/RegistrationsPanel";
import PlansEditor from "@/components/PlansEditor";
import TeamPanel from "@/pages/TeamPanel";
import UMLPanel from "@/components/UMLPanel";
import Brand from "@/components/Brand";
import { ThemeToggle } from "@/components/ThemeToggle";

function StatCard({ label, value, testId }) {
  return (
    <div className="premium-card accent-bar relative overflow-hidden p-6 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid={testId}>
      <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400 tracking-wider">{label}</div>
      <div className="mt-2 text-3xl font-black neon-text">{value}</div>
    </div>
  );
}

function ClientPayments() {
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paying, setPaying] = useState(null);

  const load = () =>
    api
      .get("/payments/my")
      .then((r) => {
        setItems(r.data.items);
        setPayments(r.data.payments);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  if (!items.length && !payments.length) return null;

  const paidItemIds = new Set(payments.filter((p) => p.status === "paid").map((p) => p.item_id));

  const pay = async (item) => {
    setPaying(item.id);
    try {
      await payItem(item, {
        onPaid: () => {
          toast.success("Payment received — thank you!");
          load();
        },
      });
    } catch (e) {
      if (e.message !== "Payment cancelled") {
        toast.error(e.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : e.message);
      }
    } finally {
      setPaying(null);
    }
  };

  return (
    <div className="mt-6" data-testid="client-payments-card">
      <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400 mb-2">Payments</div>
      {items.length > 0 && (
        <ul className="space-y-1.5" data-testid="client-payments-list">
          {items.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60" data-testid={`client-payment-item-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-900 dark:text-white font-semibold truncate">{i.title}</div>
                {i.description && <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{i.description}</div>}
              </div>
              <span className="text-sm font-bold text-cyan-700 dark:text-cyan-300">{inr(i.amount)}</span>
              {paidItemIds.has(i.id) ? (
                <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/40 code-font" data-testid={`client-payment-paid-${i.id}`}>PAID</span>
              ) : (
                <button
                  type="button"
                  onClick={() => pay(i)}
                  disabled={paying === i.id}
                  className="text-[10px] px-3 py-1.5 rounded-lg bg-cyan-700 text-white font-semibold hover:bg-cyan-800 transition-colors disabled:opacity-60 code-font"
                  data-testid={`client-pay-${i.id}`}
                >
                  {paying === i.id ? "Opening…" : "Pay Now"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {payments.filter((p) => p.status === "paid").length > 0 && (
        <div className="mt-3 space-y-1" data-testid="client-payment-history">
          {payments.filter((p) => p.status === "paid").map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-[11px] code-font text-slate-500 dark:text-slate-400">
              <span className="text-emerald-700 dark:text-emerald-300">PAID</span>
              <span className="truncate">{p.title}</span>
              <span>{inr(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentsManager() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: "", amount: "", kind: "package", project_id: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/payments/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    load();
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, [user]);

  if (user?.role !== "admin") return null;

  const inputCls =
    "px-3 py-2 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-cyan-600 text-xs transition-all";

  const create = async () => {
    if (!form.title.trim() || !form.amount) return toast.error("Enter a title and amount");
    if (form.kind === "project" && !form.project_id) return toast.error("Pick a project for a client charge");
    setBusy(true);
    try {
      await api.post("/payments/items", {
        title: form.title,
        description: form.description,
        amount_rupees: parseFloat(form.amount),
        kind: form.kind,
        project_id: form.kind === "project" ? form.project_id : null,
      });
      toast.success("Payment item added");
      setForm({ title: "", amount: "", kind: "package", project_id: "", description: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add item");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (item) => {
    await api.put(`/payments/items/${item.id}`, { active: !item.active }).catch(() => {});
    load();
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    await api.delete(`/payments/items/${item.id}`).catch(() => {});
    load();
  };

  return (
    <div className="accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] p-5" data-testid="payments-manager-card">
      <div className="text-xs uppercase code-font text-cyan-700 dark:text-cyan-300 font-bold">Payments &amp; Packages</div>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 code-font">
        Packages show on the landing page for anyone to pay; project charges appear in that client's workspace. Needs RAZORPAY_KEY_ID/SECRET on the server to accept real payments.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        <input className={inputCls} placeholder="Title (e.g. Basic Website ₹4999)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="payment-title-input" />
        <input className={inputCls} placeholder="Amount in ₹" type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" />
        <select className={inputCls} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, project_id: "" })} data-testid="payment-kind-select">
          <option value="package">Landing page package</option>
          <option value="project">Client project charge</option>
        </select>
        {form.kind === "project" ? (
          <select className={inputCls} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="payment-project-select">
            <option value="">Pick project…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name} — {p.client_email}</option>
            ))}
          </select>
        ) : (
          <input className={inputCls} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="payment-description-input" />
        )}
        <button onClick={create} disabled={busy} className="px-4 py-2 rounded-lg bg-cyan-700 text-white text-xs font-semibold hover:bg-cyan-800 transition-colors disabled:opacity-60 code-font" data-testid="payment-create-button">
          {busy ? "Adding…" : "Add Payment Item"}
        </button>
      </div>
      {items.length > 0 && (
        <ul className="mt-4 space-y-1.5" data-testid="payment-items-list">
          {items.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60" data-testid={`payment-item-${i.id}`}>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border code-font uppercase ${i.kind === "package" ? "bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700/40" : "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700/40"}`}>{i.kind}</span>
              <span className="text-xs text-slate-900 dark:text-white font-semibold">{i.title}</span>
              <span className="text-xs font-bold text-cyan-700 dark:text-cyan-300">{inr(i.amount)}</span>
              {i.client_email && <span className="text-[10px] code-font text-slate-500 dark:text-slate-400">{i.client_email}</span>}
              <span className="flex-1" />
              <button onClick={() => toggle(i)} className={`text-[10px] px-2 py-1 rounded border code-font transition-colors ${i.active ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40" : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"}`} data-testid={`payment-toggle-${i.id}`}>
                {i.active ? "Active" : "Off"}
              </button>
              <button onClick={() => remove(i)} className="text-[10px] px-2 py-1 rounded border bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 hover:bg-rose-100 code-font transition-colors" data-testid={`payment-delete-${i.id}`}>Delete</button>
            </li>
          ))}
        </ul>
      )}
      {payments.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs code-font uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="py-2 pr-4">Item</th>
                <th className="py-2 pr-4">Amount</th>
                <th className="py-2 pr-4">Payer</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.slice(0, 10).map((p) => (
                <tr key={p.id} className="border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300">
                  <td className="py-2 pr-4 text-xs text-slate-900 dark:text-white">{p.title}</td>
                  <td className="py-2 pr-4 text-xs code-font">{inr(p.amount)}</td>
                  <td className="py-2 pr-4 text-xs code-font">{p.payer_email || "—"}</td>
                  <td className="py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border code-font uppercase ${p.status === "paid" ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40" : p.status === "failed" ? "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-700/40" : "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-700/40"}`} data-testid={`payment-status-${p.id}`}>{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmployeeView({ overview, isAdmin }) {
  const { user: me } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [projects, setProjects] = useState([]);

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    loadProjects();
  }, []);

  const deployRequests = projects.filter((p) => p.deploy_requested);

  const clearDeploy = async (p) => {
    try {
      await api.put(`/projects/${p.id}/deploy-clear`);
      loadProjects();
    } catch {
      /* admin-only button */
    }
  };

  return (
    <div className="space-y-10" data-testid="employee-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
        <StatCard label="Project Enquiries" value={overview?.stats?.enquiries ?? "—"} testId="stat-enquiries" />
        <StatCard label="Consultation Bookings" value={overview?.stats?.bookings ?? "—"} testId="stat-bookings" />
        <StatCard label="Active Client Accounts" value={overview?.stats?.clients ?? "—"} testId="stat-clients" />
        <StatCard label="Deploy Requests" value={overview?.stats?.deploy_requests ?? "—"} testId="stat-deploy-requests" />
      </div>

      {deployRequests.length > 0 && (
        <div className="rounded-xl bg-amber-500/5 border border-amber-700/40 p-5" data-testid="deploy-requests-card">
          <div className="text-xs uppercase code-font text-amber-800 dark:text-amber-300 font-bold mb-3">Production Deploy Requests</div>
          {deployRequests.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-200 dark:border-slate-800 last:border-0">
              <div>
                <span className="text-sm text-slate-900 dark:text-white font-semibold">{p.name}</span>
                <span className="ml-2 text-xs text-slate-500 dark:text-slate-400 code-font">by {p.deploy_requested_by}</span>
              </div>
              {me?.role === "admin" && (
                <button
                  onClick={() => clearDeploy(p)}
                  className="text-xs px-3 py-1 rounded border bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 code-font transition-colors"
                  data-testid={`deploy-clear-${p.id}`}
                >
                  Mark Deployed
                </button>
              )}
            </div>
          ))}
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 code-font">
            Ship it: use the Redeploy button to push the latest build to your domain, then mark requests as deployed.
          </p>
        </div>
      )}

      <div className="accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] overflow-hidden" data-testid="enquiries-table-card">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white code-font uppercase tracking-wider">Latest Enquiries</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400 code-font">{enquiries.length} total</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enquiries-table">
            <thead>
              <tr className="text-left text-xs code-font uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Contact</th>
                <th className="px-6 py-3">Requirement</th>
                <th className="px-6 py-3">Overview</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.length === 0 && (
                <tr><td colSpan="4" className="px-6 py-6 text-slate-500 dark:text-slate-400 text-xs code-font" data-testid="enquiries-empty">No enquiries yet.</td></tr>
              )}
              {enquiries.map((e, i) => (
                <tr key={i} className="border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300">
                  <td className="px-6 py-3 text-slate-900 dark:text-white">{e.name}</td>
                  <td className="px-6 py-3">{e.contact}</td>
                  <td className="px-6 py-3 text-cyan-800 dark:text-cyan-300 text-xs code-font">{e.requirement}</td>
                  <td className="px-6 py-3 text-slate-500 dark:text-slate-400 text-xs max-w-xs truncate">{e.overview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] overflow-hidden" data-testid="bookings-table-card">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white code-font uppercase tracking-wider">Consultation Bookings</h2>
          <span className="text-xs text-slate-500 dark:text-slate-400 code-font">{bookings.length} total</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead>
              <tr className="text-left text-xs code-font uppercase text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Contact</th>
                <th className="px-6 py-3">Day</th>
                <th className="px-6 py-3">Window</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan="4" className="px-6 py-6 text-slate-500 dark:text-slate-400 text-xs code-font" data-testid="bookings-empty">No bookings yet.</td></tr>
              )}
              {bookings.map((b, i) => (
                <tr key={i} className="border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300">
                  <td className="px-6 py-3 text-slate-900 dark:text-white">{b.name}</td>
                  <td className="px-6 py-3">{b.contact}</td>
                  <td className="px-6 py-3 code-font text-xs">{b.date}</td>
                  <td className="px-6 py-3 text-emerald-700 dark:text-emerald-300 text-xs code-font">{b.time_slot}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <PaymentsManager />
      <RegistrationsPanel isAdmin={isAdmin} />
      {isAdmin && <PlansEditor />}
    </div>
  );
}

function ClientView({ overview, user }) {
  const project = overview?.project;
  const [files, setFiles] = useState([]);
  const [myPlan, setMyPlan] = useState(null);

  useEffect(() => {
    if (!project) return;
    api.get(`/projects/${project.id}/files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [project]);

  useEffect(() => {
    api.get("/my-plan").then((r) => setMyPlan(r.data)).catch(() => {});
  }, []);

  const downloadFile = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  const downloadPdf = async () => {
    if (!project) return;
    try {
      const res = await api.get(`/projects/${project.id}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.name}-design.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Design PDF downloaded");
    } catch (err) {
      toast.error(await formatBlobError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="client-dashboard">
      <div className="lg:col-span-7 premium-card rounded-xl bg-white dark:bg-slate-900/80 p-6 glow-border">
        <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800 text-xs code-font text-slate-500 dark:text-slate-400">
          <span>client.dreamssoft.internal</span>
          <span className="text-cyan-700 dark:text-cyan-300">CONNECTED</span>
        </div>
        {project ? (
          <div className="mt-6" data-testid="client-project-card">
            <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400">Your Project</div>
            <h2 className="mt-2 text-2xl font-bold text-slate-900 dark:text-white" data-testid="client-project-name">{project.name}</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              <span className="px-3 py-1 rounded bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-300 text-xs code-font" data-testid="client-project-status">{project.status}</span>
              <span className="px-3 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs code-font border border-slate-300 dark:border-slate-700">{project.stage}</span>
            </div>
            {project.deploy_url && (
              <a
                href={project.deploy_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-xs font-semibold px-4 py-2 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/40 hover:bg-emerald-500/25 transition-colors code-font"
                data-testid="client-open-project-link"
              >
                Open Live Project ↗
              </a>
            )}
            <button
              type="button"
              onClick={downloadPdf}
              className="mt-4 ml-2 inline-block text-xs font-semibold px-4 py-2 rounded bg-cyan-500/15 text-cyan-800 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-700/40 hover:bg-cyan-500/25 transition-colors code-font"
              data-testid="client-download-pdf-button"
            >
              Download Design PDF
            </button>
            {project.has_apk && project.share_token && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/p/${project.share_token}/app.apk`}
                className="mt-4 ml-2 inline-block text-xs font-semibold px-4 py-2 rounded bg-green-500/15 text-emerald-700 dark:text-emerald-300 border border-green-700/40 hover:bg-green-500/25 transition-colors code-font"
                data-testid="client-download-apk-link"
              >
                Download Android App
              </a>
            )}
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{project.notes}</p>
            <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 code-font">
              Last update: {project.updated_at ? new Date(project.updated_at).toLocaleString() : "—"}
            </p>
            <div className="mt-6">
              <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400 mb-2">Live Preview</div>
              {project.html ? (
                <iframe
                  sandbox="allow-scripts"
                  srcDoc={project.html}
                  title="Your site preview"
                  className="w-full aspect-video rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                  data-testid="client-preview-frame"
                />
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400 code-font" data-testid="client-preview-empty">
                  Your engineer hasn't published a build yet — check back soon.
                </p>
              )}
            </div>
            {files.length > 0 && (
              <div className="mt-6" data-testid="client-files-section">
                <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400 mb-2">Project Files</div>
                <ul className="space-y-1.5" data-testid="client-files-list">
                  {files.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 px-3 py-2 rounded bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800" data-testid={`client-file-item-${f.id}`}>
                      <span className="flex-1 min-w-0 truncate text-xs text-slate-700 dark:text-slate-200">{f.filename}</span>
                      <button type="button" onClick={() => downloadFile(f)} className="text-[10px] px-2 py-1 rounded border bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-700/40 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 code-font transition-colors" data-testid={`client-file-download-${f.id}`}>Download</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6" data-testid="client-uml-section">
              <UMLPanel projectId={project.id} />
            </div>
            <ClientPayments />
          </div>
        ) : (
          <p className="mt-6 text-sm text-slate-500 dark:text-slate-400" data-testid="client-no-project">
            No project is linked to your account yet. Your engineer will attach your workspace after kickoff.
          </p>
        )}
      </div>
      <div className="lg:col-span-5 space-y-6">
        {myPlan?.plan && (
          <div className="accent-bar premium-card relative overflow-hidden p-6 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]" data-testid="client-plan-card">
            <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">Your Plan</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white" data-testid="client-plan-name">{myPlan.plan.name}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 code-font">{myPlan.plan.price}</span>
            </div>
            <div className="mt-3 space-y-2 text-xs text-slate-600 dark:text-slate-300">
              <div className="flex justify-between" data-testid="client-plan-projects">
                <span>Projects</span>
                <span className="code-font font-semibold">
                  {myPlan.projects_used}{myPlan.plan.max_projects ? ` / ${myPlan.plan.max_projects}` : " · unlimited"}
                </span>
              </div>
              <div className="flex justify-between" data-testid="client-plan-gens">
                <span>AI drafts & videos this month</span>
                <span className="code-font font-semibold">
                  {myPlan.gens_used}{myPlan.plan.monthly_gens ? ` / ${myPlan.plan.monthly_gens}` : " · unlimited"}
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="premium-card p-6 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]">
          <div className="text-xs uppercase code-font text-cyan-700 dark:text-cyan-300 font-bold">Direct Support</div>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
            You speak directly with the engineers building your project — no account managers, no ticket queues.
          </p>
          <Link
            to="/#contact"
            className="mt-4 inline-block text-xs font-semibold px-4 py-2 rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-700 transition-colors code-font"
            data-testid="client-contact-team"
          >
            Message The Team
          </Link>
        </div>
        <div className="premium-card p-6 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08]">
          <div className="text-xs uppercase code-font text-slate-500 dark:text-slate-400">Signed In As</div>
          <p className="mt-2 text-sm text-slate-900 dark:text-white font-semibold">{user?.name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 code-font">{user?.email}</p>
        </div>
      </div>
    </div>
  );
}

export default function PortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("deck");

  useEffect(() => {
    api.get("/portal/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isEmployee = user && (user.role === "employee" || user.role === "admin");
  const isAdmin = user && user.role === "admin";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white" data-testid="portal-dashboard">
      <nav className="sticky top-0 z-40 bg-white/85 dark:bg-[#080C14]/80 backdrop-blur-xl border-b border-slate-200/80 dark:border-white/[0.06] px-6 py-3.5 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Brand subtitle="Work Desk" />
            <span className="hidden sm:inline px-2 py-0.5 rounded bg-cyan-50 dark:bg-cyan-500/10 text-cyan-800 dark:text-cyan-300 text-xs code-font uppercase" data-testid="portal-role-badge">
              {isAdmin ? "Admin Hub" : isEmployee ? "Employee Hub" : "Client Area"}
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="hidden md:inline text-xs text-slate-500 dark:text-slate-400 code-font" data-testid="portal-user-email">{user?.email}</span>
            <ThemeToggle />
            <button
              onClick={handleLogout}
              className="text-xs code-font px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-900 hover:border-slate-300 transition-colors"
              data-testid="portal-logout-button"
            >
              Log Out
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-cyan-700 dark:text-cyan-300 code-font font-bold">Dreams Soft Solutions Work Desk</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white" data-testid="portal-heading">
            {isEmployee ? (<>Engineering <span className="neon-text">Command Deck</span></>) : `Welcome back, ${user?.name || "Client"}`}
          </h1>
        </div>
        {isEmployee && (
          <div className="inline-flex gap-1 mb-8 p-1 rounded-xl bg-slate-100 dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06]" data-testid="portal-tabs">
            <button
              onClick={() => setTab("deck")}
              className={`text-xs code-font px-4 py-2 rounded-lg transition-all ${
                tab === "deck"
                  ? "bg-white dark:bg-cyan-400/15 text-cyan-900 dark:text-cyan-200 shadow-sm dark:shadow-[0_0_18px_-4px_rgba(0,240,255,0.45)] font-semibold border border-slate-200 dark:border-cyan-400/30"
                  : "text-slate-600 dark:text-slate-300 border border-transparent hover:text-slate-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-white/[0.06]"
              }`}
              data-testid="tab-command-deck"
            >
              Command Deck
            </button>
            <button
              onClick={() => setTab("workbench")}
              className={`text-xs code-font px-4 py-2 rounded-lg transition-all ${
                tab === "workbench"
                  ? "bg-white dark:bg-cyan-400/15 text-cyan-900 dark:text-cyan-200 shadow-sm dark:shadow-[0_0_18px_-4px_rgba(0,240,255,0.45)] font-semibold border border-slate-200 dark:border-cyan-400/30"
                  : "text-slate-600 dark:text-slate-300 border border-transparent hover:text-slate-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-white/[0.06]"
              }`}
              data-testid="tab-workbench"
            >
              Workbench
            </button>
            {isAdmin && (
              <button
                onClick={() => setTab("team")}
                className={`text-xs code-font px-4 py-2 rounded-lg transition-all ${
                  tab === "team"
                    ? "bg-white dark:bg-cyan-400/15 text-cyan-900 dark:text-cyan-200 shadow-sm dark:shadow-[0_0_18px_-4px_rgba(0,240,255,0.45)] font-semibold border border-slate-200 dark:border-cyan-400/30"
                    : "text-slate-600 dark:text-slate-300 border border-transparent hover:text-slate-900 dark:hover:text-white hover:bg-white/70 dark:hover:bg-white/[0.06]"
                }`}
                data-testid="tab-team"
              >
                Team Access
              </button>
            )}
          </div>
        )}
        {isEmployee ? (
          tab === "deck" ? <EmployeeView overview={overview} isAdmin={isAdmin} /> : tab === "workbench" ? <Workbench onOpenTeam={isAdmin ? () => setTab("team") : null} /> : <TeamPanel />
        ) : (
          <ClientView overview={overview} user={user} />
        )}
      </main>
    </div>
  );
}
